import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatRegimenSatSelectLabel, REGIMENES_SAT } from "@/lib/regimenesSat";
import { formatRequestedModulesLabel } from "@/lib/commercialLicenseRequests";
import {
  CFDI_USE_OPTIONS,
  DEFAULT_INVOICE_CFDI_USE,
  DEFAULT_INVOICE_PAYMENT_METHOD,
  PAYMENT_METHOD_OPTIONS,
} from "@/lib/commercialInvoiceCatalog";

/**
 * Bloque reutilizable: ¿Requiere factura? + campos fiscales.
 */
export default function LicenseInvoiceSection({
  form,
  setForm,
  profile = {},
  disabled = false,
  onApplyRegistrationData,
  showModuleSummary = false,
  invoiceQuestion = "¿Requieres factura por esta contratación?",
  testId = "license-invoice-section",
}) {
  const applyRegistrationInvoiceData = (checked) => {
    if (onApplyRegistrationData) {
      onApplyRegistrationData(checked);
      return;
    }
    setForm((prev) => ({
      ...prev,
      use_registration_invoice_data: checked,
      invoice_rfc: checked ? (profile.rfc || prev.rfc || "").trim().toUpperCase() : prev.invoice_rfc,
      invoice_razon_social: checked
        ? (profile.razon_social || prev.razon_social || "").trim()
        : prev.invoice_razon_social,
      invoice_email: checked
        ? (profile.correo_facturacion || profile.email || prev.email || "").trim()
        : prev.invoice_email,
      invoice_codigo_postal: checked
        ? (profile.codigo_postal || prev.invoice_codigo_postal || "").trim()
        : prev.invoice_codigo_postal,
      invoice_fiscal_address: checked
        ? (profile.domicilio || prev.invoice_fiscal_address || "").trim()
        : prev.invoice_fiscal_address,
      invoice_regimen_fiscal: checked
        ? (profile.regimen_fiscal || prev.invoice_regimen_fiscal || "").trim()
        : prev.invoice_regimen_fiscal,
      invoice_cfdi_use: checked ? DEFAULT_INVOICE_CFDI_USE : prev.invoice_cfdi_use,
      invoice_payment_method: checked ? DEFAULT_INVOICE_PAYMENT_METHOD : prev.invoice_payment_method,
    }));
  };

  return (
    <section className="space-y-2" data-testid={testId}>
      <h5 className="text-xs font-semibold text-[#0F2D5C] m-0">{invoiceQuestion}</h5>
      <RadioGroup
        value={form.requires_invoice ? "yes" : "no"}
        onValueChange={(v) =>
          setForm((f) => ({
            ...f,
            requires_invoice: v === "yes",
            ...(v === "yes" && !f.invoice_cfdi_use
              ? { invoice_cfdi_use: DEFAULT_INVOICE_CFDI_USE }
              : {}),
            ...(v === "yes" && !f.invoice_payment_method
              ? { invoice_payment_method: DEFAULT_INVOICE_PAYMENT_METHOD }
              : {}),
            ...(v === "yes" && !(f.invoice_fiscal_address || "").trim()
              ? { invoice_fiscal_address: (profile.domicilio || "").trim() }
              : {}),
          }))
        }
        className="flex flex-wrap gap-4 text-[11px]"
      >
        <label className="flex items-center gap-2 cursor-pointer">
          <RadioGroupItem value="no" disabled={disabled} />
          No
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <RadioGroupItem value="yes" disabled={disabled} />
          Sí
        </label>
      </RadioGroup>
      {form.requires_invoice ? (
        <div className="space-y-3 pt-2 border-t border-slate-200">
          <p className="text-[10px] text-slate-600 m-0">
            BNT usará estos datos para facturarte manualmente en otro sistema. No se timbra CFDI desde aquí.
          </p>
          <label className="flex items-center gap-2 text-[11px] cursor-pointer">
            <Checkbox
              checked={Boolean(form.use_registration_invoice_data)}
              onCheckedChange={(v) => applyRegistrationInvoiceData(v === true)}
              disabled={disabled}
            />
            Usar los mismos datos del comprador
          </label>
          {showModuleSummary ? (
            <p className="text-[10px] text-slate-600 m-0">
              <span className="font-medium">Módulos solicitados:</span>{" "}
              {formatRequestedModulesLabel(form.requested_modules) || "—"}
            </p>
          ) : null}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">RFC facturación</Label>
              <Input
                value={form.invoice_rfc}
                onChange={(e) => setForm((f) => ({ ...f, invoice_rfc: e.target.value.toUpperCase() }))}
                disabled={disabled}
                className="mt-1 h-9 text-xs font-mono"
                data-testid="license-invoice-rfc"
              />
            </div>
            <div>
              <Label className="text-xs">Nombre / razón social</Label>
              <Input
                value={form.invoice_razon_social}
                onChange={(e) => setForm((f) => ({ ...f, invoice_razon_social: e.target.value }))}
                disabled={disabled}
                className="mt-1 h-9 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Régimen fiscal</Label>
              <Select
                value={form.invoice_regimen_fiscal || "__none__"}
                onValueChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    invoice_regimen_fiscal: v === "__none__" ? "" : v,
                  }))
                }
                disabled={disabled}
              >
                <SelectTrigger className="mt-1 h-9 text-xs" data-testid="license-invoice-regimen">
                  <SelectValue placeholder="Seleccione régimen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {REGIMENES_SAT.map((r) => (
                    <SelectItem key={r.c} value={r.c}>
                      {formatRegimenSatSelectLabel(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Código postal fiscal</Label>
              <Input
                value={form.invoice_codigo_postal}
                onChange={(e) => setForm((f) => ({ ...f, invoice_codigo_postal: e.target.value }))}
                disabled={disabled}
                className="mt-1 h-9 text-xs font-mono"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Domicilio fiscal</Label>
              <SpellcheckTextarea
                value={form.invoice_fiscal_address || ""}
                onChange={(e) => setForm((f) => ({ ...f, invoice_fiscal_address: e.target.value }))}
                disabled={disabled}
                rows={2}
                className="mt-1 rounded-sm text-xs resize-none"
                testId="license-invoice-fiscal-address"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Correo para envío de factura</Label>
              <Input
                type="email"
                value={form.invoice_email}
                onChange={(e) => setForm((f) => ({ ...f, invoice_email: e.target.value }))}
                disabled={disabled}
                className="mt-1 h-9 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Uso de CFDI</Label>
              <Select
                value={form.invoice_cfdi_use || DEFAULT_INVOICE_CFDI_USE}
                onValueChange={(v) => setForm((f) => ({ ...f, invoice_cfdi_use: v }))}
                disabled={disabled}
              >
                <SelectTrigger className="mt-1 h-9 text-xs" data-testid="license-invoice-cfdi-use">
                  <SelectValue placeholder="Seleccione uso de CFDI" />
                </SelectTrigger>
                <SelectContent>
                  {CFDI_USE_OPTIONS.map((o) => (
                    <SelectItem key={o.code} value={o.code}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Forma de pago</Label>
              <Select
                value={form.invoice_payment_method || DEFAULT_INVOICE_PAYMENT_METHOD}
                onValueChange={(v) => setForm((f) => ({ ...f, invoice_payment_method: v }))}
                disabled={disabled}
              >
                <SelectTrigger className="mt-1 h-9 text-xs" data-testid="license-invoice-payment-method">
                  <SelectValue placeholder="Seleccione forma de pago" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHOD_OPTIONS.map((o) => (
                    <SelectItem key={o.code} value={o.code}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Comentarios de facturación (opcional)</Label>
            <SpellcheckTextarea
              value={form.invoice_comments || ""}
              onChange={(e) => setForm((f) => ({ ...f, invoice_comments: e.target.value }))}
              disabled={disabled}
              rows={2}
              className="mt-1 rounded-sm text-xs resize-none"
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
